import { useState, useEffect, useCallback } from 'react';

import { usePreferencesStore } from '#lib/stores/preferencesStore';

/**
 * Hook to manage search query selection
 * Handles auto-selection of queries
 *
 * @param {Array} leadSearchQueries - Lead search queries
 * @returns {Object} - Selected query state and handlers
 */
export const useSearchQueryManagement = (leadSearchQueries) => {
	const storedQueryId = usePreferencesStore((s) => s.lastSelectedSearchQueryId);
	const setStoredQueryId = usePreferencesStore((s) => s.setLastSelectedSearchQueryId);

	const [selectedSearchQueryId, setSelectedSearchQueryIdRaw] = useState(storedQueryId);

	const setSelectedSearchQueryId = useCallback(
		(id) => {
			setSelectedSearchQueryIdRaw(id);
			setStoredQueryId(id);
		},
		[setStoredQueryId],
	);

	const searchQueries = leadSearchQueries;

	// Auto-select search query
	useEffect(() => {
		if (!selectedSearchQueryId && searchQueries.length) {
			setSelectedSearchQueryId(searchQueries[0].id);
		}
	}, [selectedSearchQueryId, searchQueries.length, searchQueries, setSelectedSearchQueryId]);

	// When queries change, ensure selection is valid
	useEffect(() => {
		if (searchQueries.length > 0) {
			const currentQueryExists = searchQueries.some((q) => q.id === selectedSearchQueryId);
			if (!selectedSearchQueryId || !currentQueryExists) {
				setSelectedSearchQueryId(searchQueries[0].id);
			}
		} else {
			setSelectedSearchQueryId(null);
		}
	}, [searchQueries.length, searchQueries, selectedSearchQueryId, setSelectedSearchQueryId]);

	const handleSearchQuerySelect = useCallback(
		(queryId) => {
			setSelectedSearchQueryId(queryId);
		},
		[setSelectedSearchQueryId],
	);

	const selectedSearchQuery = searchQueries.find((q) => q.id === selectedSearchQueryId);

	return {
		selectedSearchQueryId,
		selectedSearchQuery,
		handleSearchQuerySelect,
		searchQueries,
	};
};
